import React, { useState, useEffect, useCallback } from 'react';
import {
	HStack,
	Box,
	Image,
	Text,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	MenuDivider,
	MenuGroup,
	Flex,
	Button,
	useDisclosure,
	useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { LOGO_SRC as Headerlogo } from '../theme/brand';
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../index.css";
import { fetchWrapper } from '../helpers';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { FiAlertCircle } from 'react-icons/fi';
import { BsShield } from 'react-icons/bs';
import { TwoFactorAuthModal } from './TwoFaModal';

function Header({ header }) {

	

	const [email, setEmail] = useState('');
	const [permissions, setPermissions] = useState([]);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const location = useLocation();
	const navigate = useNavigate();

	/**
	 * Is `slug` the nav entry for the current route?
	 *
	 * Matches on PATH SEGMENTS, never as a substring. The parent-dropdown check used
	 * `location.pathname.includes(child.slug)`, so a slug that happens to be a prefix of
	 * another route lit up two menus at once: on /documentation/webhook-doc, the group
	 * holding `webhook` matched because "/documentation/webhook-doc".includes("webhook")
	 * is true. Requiring either an exact match or a following "/" fixes that while
	 * keeping detail routes (/insights/123 → `insights`) highlighted under their parent.
	 *
	 * Note "/webhook-doc" does NOT match `webhook`: a hyphen is not a segment boundary,
	 * which is the whole point.
	 */
	const isNavActive = useCallback((slug) => {
		if (!slug) return false;
		const target = `/${String(slug).replace(/^\/+/, '')}`;
		return location.pathname === target || location.pathname.startsWith(`${target}/`);
	}, [location.pathname]);

	/* ---------------- 2FA BANNER (ONE TIME) ---------------- */
	const [showBanner, setShowBanner] = useState(() => {
		const mfaEnabled = localStorage.getItem('mfa_enabled') === 'true';
		const mfaVerified = localStorage.getItem('mfa_verified') === 'true';
		const twofaRequired = localStorage.getItem('twofa_required') === 'false';

		return !twofaRequired && !(mfaEnabled && mfaVerified);
	});

	const handleSuccess = () => {
		setShowBanner(false);
	};

	/* ---------------- TOKEN DECODE (SINGLE UPDATE) ---------------- */
	useEffect(() => {
		const token = localStorage.getItem('customer-portal');
		if (!token) return;

		try {
			const decodedToken = jwtDecode(token);
			const Userdata = decodedToken?.userdata;

			if (!Userdata) return;

			// ✅ batch state update
			setEmail(Userdata.username || '');
			setPermissions(Userdata.permission || []);

			localStorage.setItem("customer", JSON.stringify(Userdata));
		} catch (error) {
			console.error("Failed to decode token:", error);
			toast.error("Invalid session, please log in again.");
		}
	}, []); // ❌ removed navigate dependency

	/* ---------------- LOGOUT ---------------- */
	const Logout = useCallback(async () => {
		const url = `${process.env.REACT_APP_API_URL}/user/logout`;
		const res = await fetchWrapper.post(url, {});

		if (res?.success) {
			localStorage.removeItem('user');
			localStorage.removeItem('customer-portal');
			navigate("/");
		} else {
			toast.error("Logout failed");
		}
	}, [navigate]);

	/* ---------------- MENU RENDER (NO JSX CHANGE) ---------------- */
	const renderMenuItem = useCallback((page) => {
		if (!page.children || page.children.length === 0) {
			return (
				<Box me="18px" key={page.id} flexShrink={0}>
					<Link
						to={`/${page.slug}`}
						className={isNavActive(page.slug) ? "active" : ""}
						id='top-header-menu'
					>
						{page.name}
					</Link>
				</Box>
			);
		} else {
			return (
				<Box me="18px" key={page.id} flexShrink={0}>
					<Menu>
						<MenuButton
							textAlign="left"
							fontSize="14px"
							className={page.children.some(child => isNavActive(child.slug)) ? "activeclass" : ""}
						>
							<HStack id="Header_User">
								<Box id='top-header-menu'>{page.name}</Box>
								<Box><ChevronDownIcon boxSize={6} /></Box>
							</HStack>
						</MenuButton>
						<MenuList style={{ paddingBottom: "1px", paddingTop: "1px" }}>
							{page.children.map((child) => (
								<MenuItem mb="2px" key={child.id}>
									<Link
										to={`/${child.slug}`}
										className={isNavActive(child.slug) ? "activeclass-menu" : ""}
										id='top-header-menu'
									>
										{child.name}
									</Link>
								</MenuItem>
							))}
						</MenuList>
					</Menu>
				</Box>
			);
		}
		// isNavActive already closes over location.pathname, so this is the only dep needed.
	}, [isNavActive]);

	const NAV_VISIBLE = useBreakpointValue({ base: 2, sm: 4, md: 6, lg: 9, xl: 12, '2xl': 999 }) ?? 12;

	return (
		<>
			{showBanner && (
				<Box
					bgGradient="linear(to-r, orange.500, yellow.500)"
					borderBottom="1px"
					borderColor="orange.600"
				>
					<Box mx="auto" px={16} py={3}>
						<Flex alignItems="center" justifyContent="space-between">
							<Flex alignItems="center" gap={3}>
								<Box bg="whiteAlpha.200" borderRadius="full" p={1.5}>
									<FiAlertCircle size={20} color="white" />
								</Box>
								<Text color="white" fontSize="sm">
									<strong>Security Alert:</strong> Two-Factor Authentication is required for your account. Please set up 2FA using Google Authenticator to secure your account.
								</Text>
							</Flex>
							<Button
								size="sm"
								onClick={onOpen}
								bg="white"
								color="#f54a00"
								_hover={{ bg: '#fff7ed !important', color: '#f97316 !important' }}
								flexShrink={0}
								ml={4}
								leftIcon={<BsShield size={16} />}
							>
								Enable 2FA
							</Button>
						</Flex>
					</Box>
				</Box>
			)}

			{/* Top Header */}
			<HStack px="70px" py="10px" justifyContent="space-between" className="top_header_section">
				<Box>
					<Link to="/dashboard">
						{/* Logo comes from REACT_APP_LOGO_URL, so its aspect ratio is unknown at
						    build time. Bound BOTH axes and let objectFit scale it down to fit:
						    a wide lockup fills the width, a square mark stays square. Never set
						    only one axis here — that stretches or shrinks off-brand logos. */}
						<Image src={Headerlogo} alt='Logo' w="auto" h="auto" maxH="36px" maxW="150px" objectFit="contain" />
					</Link>
				</Box>
				<Box>
					<HStack>
						<Box>
							<Menu>
								<MenuButton textAlign="left" fontSize="14px">
									<Text mb="0">Welcome</Text>
									<HStack id="Header_User">
										<Box>{email}</Box>
										<Box><ChevronDownIcon boxSize={6} /></Box>
									</HStack>
								</MenuButton>
								<MenuList>
									<MenuItem onClick={Logout}>Logout</MenuItem>
								</MenuList>
							</Menu>
						</Box>
					</HStack>
				</Box>
			</HStack>

			{/* Navigation Menu */}
			<HStack
				h="50px"
				bg="#303030"
				py="30px"
				justifyContent="center"
				alignItems="center"
				color="black"
				fontSize="14px"
				className="header_section"
				borderBottom={"1px"}
				borderColor={"#0000001a"}
				flexWrap="nowrap"
				overflow="visible"
			>
				{permissions.slice(0, NAV_VISIBLE).map(renderMenuItem)}
				{permissions.length > NAV_VISIBLE && (
					<Box flexShrink={0}>
						<Menu>
							<MenuButton fontSize="14px" id="top-header-menu">
								<HStack id="Header_User" spacing={1}>
									<Box id="top-header-menu">More</Box>
									<ChevronDownIcon boxSize={5} />
								</HStack>
							</MenuButton>
							<MenuList
								maxH="70vh"
								overflowY="auto"
								zIndex={9999}
								minW="200px"
								style={{ paddingBottom: '4px', paddingTop: '4px' }}
							>
								{permissions.slice(NAV_VISIBLE).map((page, idx) => {
									if (page.children?.length > 0) {
										return (
											<Box key={page.id}>
												{idx > 0 && <MenuDivider my={1} />}
												<MenuGroup
													title={page.name}
													fontSize="11px"
													fontWeight="700"
													color="gray.400"
													textTransform="uppercase"
													letterSpacing="wide"
													ml={3}
													mt={1}
													mb={0}
												>
													{page.children.map(child => (
														<MenuItem key={child.id} py={1.5} fontSize="13px">
															<Link
																to={`/${child.slug}`}
																className={isNavActive(child.slug) ? 'activeclass-menu' : ''}
																id="top-header-menu"
																style={{ width: '100%' }}
															>
																{child.name}
															</Link>
														</MenuItem>
													))}
												</MenuGroup>
											</Box>
										);
									}
									return (
										<MenuItem key={page.id} py={1.5} fontSize="13px">
											<Link
												to={`/${page.slug}`}
												className={isNavActive(page.slug) ? 'activeclass-menu' : ''}
												id="top-header-menu"
												style={{ width: '100%' }}
											>
												{page.name}
											</Link>
										</MenuItem>
									);
								})}
							</MenuList>
						</Menu>
					</Box>
				)}
			</HStack>

			<TwoFactorAuthModal isOpen={isOpen} onClose={onClose} />
		</>
	);
}

export default Header;
